import React, { useEffect, useState, useCallback, useMemo, useContext } from "react";
import {
  makeStyles,
  Grid,
  Paper,
  Typography,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Switch,
  FormControlLabel,
  Tooltip,
  TableSortLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Alert from "@material-ui/lab/Alert";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartTooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import SearchIcon from "@material-ui/icons/Search";
import EditIcon from "@material-ui/icons/Edit";
import GroupIcon from "@material-ui/icons/Group";
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import VisibilityIcon from "@material-ui/icons/Visibility";
import DeleteSweepIcon from "@material-ui/icons/DeleteSweep";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import SettingsIcon from "@material-ui/icons/Settings";
import AddIcon from "@material-ui/icons/Add";
import RefreshIcon from "@material-ui/icons/Refresh";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n.js";
import CompanyEditModal from "../../components/CompanyEditModal";
import UserSelectorModal from "../../components/UserSelectorModal";
import { AuthContext } from "../../context/Auth/AuthContext";

// Color per operationStatus (feat_231).
const OPERATION_STATUS_META = {
  active: { color: "#22c55e" },
  paused: { color: "#9ca3af" },
  blocked: { color: "#ef4444" },
  waiting_meta: { color: "#f59e0b" },
  demo: { color: "#3b82f6" },
  trial: { color: "#38bdf8" },
};
const OP_STATUS_KEYS = ["active", "paused", "blocked", "waiting_meta", "demo", "trial"];

const REFRESH_MS = 60 * 1000;

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    height: "100%",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  metricCard: { padding: theme.spacing(1), textAlign: "center" },
  metricValue: {
    fontSize: 24,
    fontWeight: "bold",
    color: theme.palette.primary.main,
    lineHeight: 1.1,
  },
  metricLabel: { color: theme.palette.text.secondary, fontSize: 11 },
  systemLine: {
    padding: theme.spacing(0.75, 1.5),
    marginTop: theme.spacing(1),
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    fontSize: 13,
    alignItems: "center",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    flexWrap: "wrap",
  },
  filterChips: { display: "flex", gap: theme.spacing(0.5), flexWrap: "wrap" },
  tableWrap: { flex: 1, minHeight: 0, overflow: "auto" },
  stickyHead: {
    "& th": {
      position: "sticky",
      top: 0,
      zIndex: 2,
      backgroundColor: theme.palette.background.paper,
    },
  },
  zebra: {
    "& tbody tr:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  actionsCell: { whiteSpace: "nowrap" },
  statusDot: {
    display: "inline-block",
    width: 8,
    height: 8,
    borderRadius: "50%",
    marginRight: 6,
    verticalAlign: "middle",
  },
  onlineDot: {
    display: "inline-block",
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: "#22c55e",
    marginLeft: 4,
  },
}));

const MetricCard = ({ label, value, classes }) => (
  <Paper className={classes.metricCard}>
    <Typography className={classes.metricValue}>
      {value ?? <CircularProgress size={18} />}
    </Typography>
    <Typography className={classes.metricLabel}>{label}</Typography>
  </Paper>
);

const StatusChip = ({ status }) => {
  const meta = OPERATION_STATUS_META[status] || OPERATION_STATUS_META.active;
  return (
    <Chip
      size="small"
      variant="outlined"
      label={
        <span>
          <span
            style={{
              display: "inline-block",
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: meta.color,
              marginRight: 6,
              verticalAlign: "middle",
            }}
          />
          {i18n.t(`operationStatus.${status}`)}
        </span>
      }
    />
  );
};

const applyImpersonationToken = (backupToken, companyName, data) => {
  localStorage.setItem("token_superadmin_backup", backupToken);
  localStorage.setItem("impersonating_company", companyName);
  localStorage.setItem("token", JSON.stringify(data.token));
  if (data.refreshToken) {
    localStorage.setItem("impersonating_refresh_token", data.refreshToken);
  }
  api.defaults.headers.Authorization = `Bearer ${data.token}`;
};

// feat_242: buscador 100% aislado. React.memo + id único + guard de focus que
// rechaza cambios que NO vengan del propio input (bloquea autofill del browser,
// que ignora autocomplete=off y escribía emails de otros campos al buscador).
const SEARCH_INPUT_ID = "panel-general-search-v242";
const SearchInput = React.memo(({ value, onChange }) => {
  const handleChange = (e) => {
    const v = e.target.value;
    // Solo aceptar cambios originados por foco real en este input.
    if (document.activeElement?.id !== SEARCH_INPUT_ID) return;
    onChange(v);
  };
  return (
    <TextField
      size="small"
      variant="outlined"
      name="panel-general-search-v242"
      placeholder={i18n.t("panel.search")}
      value={value}
      onChange={handleChange}
      style={{ minWidth: 250 }}
      autoComplete="new-password"
      inputProps={{
        id: SEARCH_INPUT_ID,
        autoComplete: "new-password",
        "data-lpignore": "true",
        "data-form-type": "other",
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        ),
      }}
    />
  );
});

const SuperAdminPanel = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  // feat_241: eliminar empresa = solo super superadmin (sin scope de marca)
  const canDelete = !!user?.isSuperAdmin && !user?.whitelabelScopeId;

  const [summary, setSummary] = useState(null);
  const [system, setSystem] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [scope, setScope] = useState(null); // feat_236: scope de marca (reseller)
  const [allowSelfRegistration, setAllowSelfRegistration] = useState(false);
  const [savingReg, setSavingReg] = useState(false);

  // filters / sort
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [orderBy, setOrderBy] = useState("id");
  const [order, setOrder] = useState("asc");

  // full editor modal (feat_232)
  const [editModal, setEditModal] = useState({ open: false, company: null, initialTab: "general" });

  // impersonation
  const [userSelectorCompany, setUserSelectorCompany] = useState(null);
  const [adminSelectorCompany, setAdminSelectorCompany] = useState(null);
  const [impersonating, setImpersonating] = useState(false);

  // clear data
  const [clearTarget, setClearTarget] = useState(null);
  const [clearing, setClearing] = useState(false);

  // feat_241 — eliminar empresa (hard delete, confirmación por nombre exacto)
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);

  // feat_235 — monitoreo (lazy: solo carga al expandir el accordion)
  const [monitoring, setMonitoring] = useState({ history: [], prediction: null });
  const [monitoringLoaded, setMonitoringLoaded] = useState(false);

  const loadMonitoring = useCallback(async () => {
    if (monitoringLoaded) return;
    try {
      const [{ data: history }, { data: prediction }] = await Promise.all([
        api.get("/superadmin/system-health/history?days=30"),
        api.get("/superadmin/system-health/prediction"),
      ]);
      setMonitoring({ history, prediction });
      setMonitoringLoaded(true);
    } catch (err) {
      toastError(err);
    }
  }, [monitoringLoaded]);

  const fetchOverview = useCallback(async () => {
    try {
      const { data } = await api.get("/superadmin/overview");
      setSummary(data.summary);
      setSystem(data.system);
      setCompanies(data.companies || []);
      setScope(data.scope || null);
      setAllowSelfRegistration(data.config?.allowSelfRegistration === "true");
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    fetchOverview();
    const t = setInterval(fetchOverview, REFRESH_MS);
    return () => clearInterval(t);
  }, [fetchOverview]);

  const toggleSelfRegistration = async (newVal) => {
    setSavingReg(true);
    try {
      await api.put("/superadmin/config", {
        key: "allowSelfRegistration",
        value: String(newVal),
      });
      setAllowSelfRegistration(newVal);
      toast.success(`Registro propio ${newVal ? "habilitado" : "deshabilitado"}`);
    } catch (err) {
      toastError(err);
    }
    setSavingReg(false);
  };

  // ── editor triggers ──
  const openCreate = () =>
    setEditModal({ open: true, company: null, initialTab: "general" });
  const handleEdit = (c) =>
    setEditModal({ open: true, company: c, initialTab: "general" });
  const handleUsers = (c) =>
    setEditModal({ open: true, company: c, initialTab: "users" });
  const handleAdvanced = (c) =>
    setEditModal({ open: true, company: c, initialTab: "settings" });
  // feat_241: refetch defensivo al cerrar (guardar O cancelar) para reflejar el estado real
  const closeEditModal = () => {
    setEditModal({ open: false, company: null, initialTab: "general" });
    fetchOverview();
  };

  // ── impersonation ──
  const doImpersonate = async (companyId, companyName, userId) => {
    setImpersonating(true);
    try {
      const { data: backupData } = await api.get("/auth/impersonation-backup");
      const url = userId
        ? `/superadmin/impersonate/${companyId}?userId=${userId}`
        : `/superadmin/impersonate/${companyId}`;
      const { data } = await api.post(url);
      applyImpersonationToken(backupData.backupToken, companyName, data);
      toast.info(`Entrando como ${companyName}...`);
      window.location.href = "/tickets";
    } catch (err) {
      toastError(err);
      setImpersonating(false);
    }
  };

  // feat_233: 1 admin → directo; >1 → selector; 0 → toast.
  const handleImpersonateAdmin = async (c) => {
    try {
      const { data: admins } = await api.get(
        `/superadmin/companies/${c.id}/admins-brief`
      );
      if (!admins || admins.length === 0) {
        toast.error("Esta empresa no tiene administradores");
        return;
      }
      if (admins.length === 1) {
        doImpersonate(c.id, c.name, admins[0].id);
        return;
      }
      setAdminSelectorCompany(c);
    } catch (err) {
      toastError(err);
    }
  };
  const handleImpersonateAdminPick = (userId) => {
    if (!adminSelectorCompany) return;
    doImpersonate(adminSelectorCompany.id, adminSelectorCompany.name, userId);
  };
  const handleOpenUserSelector = (c) => setUserSelectorCompany(c);
  const handleImpersonateUser = (userId) => {
    if (!userSelectorCompany) return;
    doImpersonate(userSelectorCompany.id, userSelectorCompany.name, userId);
  };

  // ── clear ──
  const handleClearData = async () => {
    if (!clearTarget) return;
    setClearing(true);
    try {
      await api.delete(`/superadmin/companies/${clearTarget.id}/data`);
      toast.success(`Datos de ${clearTarget.name} eliminados`);
      setClearTarget(null);
      fetchOverview();
    } catch (err) {
      toastError(err);
    } finally {
      setClearing(false);
    }
  };

  // ── eliminar empresa (feat_241) ──
  const openDelete = (c) => {
    setDeleteConfirm("");
    setDeleteTarget({ id: c.id, name: c.name });
  };
  const handleDeleteCompany = async () => {
    if (!deleteTarget || deleteConfirm !== deleteTarget.name) return;
    setDeleting(true);
    try {
      // hard delete: destruye todos los datos (tickets/contactos/mensajes/usuarios/conexiones)
      await api.delete(`/companies/${deleteTarget.id}/hard`);
      toast.success(`Empresa "${deleteTarget.name}" eliminada`);
      setDeleteTarget(null);
      setDeleteConfirm("");
      fetchOverview();
    } catch (err) {
      toastError(err);
    } finally {
      setDeleting(false);
    }
  };

  // ── filter + sort ──
  const handleSort = (col) => {
    if (orderBy === col) setOrder(order === "asc" ? "desc" : "asc");
    else {
      setOrderBy(col);
      setOrder("asc");
    }
  };

  const visibleCompanies = useMemo(() => {
    let list = companies;
    const q = search.trim().toLowerCase();
    if (q) {
      // feat_242: búsqueda ampliada — id/name/slug/notes/operationStatus/whitelabel/usuarios.
      list = list.filter(
        (c) =>
          String(c.id).includes(q) ||
          (c.name || "").toLowerCase().includes(q) ||
          (c.slug || "").toLowerCase().includes(q) ||
          (c.notes || "").toLowerCase().includes(q) ||
          (c.operationStatus || "").toLowerCase().includes(q) ||
          (c.whitelabel?.name || "").toLowerCase().includes(q) ||
          (c.users || []).some(
            (u) =>
              (u.name || "").toLowerCase().includes(q) ||
              (u.email || "").toLowerCase().includes(q)
          )
      );
    }
    if (statusFilter !== "all") {
      list = list.filter((c) => (c.operationStatus || "active") === statusFilter);
    }
    const dir = order === "asc" ? 1 : -1;
    return [...list].sort((a, b) => {
      let av = a[orderBy];
      let bv = b[orderBy];
      if (orderBy === "name") {
        av = (a.name || "").toLowerCase();
        bv = (b.name || "").toLowerCase();
      }
      if (av == null) av = 0;
      if (bv == null) bv = 0;
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [companies, search, statusFilter, orderBy, order]);

  const disconnectedCompanies = companies.filter(
    (c) => c.active && c.whatsappCount > 0 && c.connectedWhatsapps === 0
  );

  const METRICS = summary
    ? [
        { label: i18n.t("panel.summary.messages2h"), value: summary.messages2h },
        { label: i18n.t("panel.summary.openTickets"), value: summary.openTickets },
        { label: i18n.t("panel.summary.activeCompanies"), value: summary.activeCompanies },
        { label: i18n.t("panel.summary.usersOnline"), value: summary.usersOnline },
        { label: i18n.t("panel.summary.totalCompanies"), value: summary.totalCompanies },
      ]
    : Array(5).fill({ label: "...", value: null });

  const sortLabel = (col, label) => (
    <TableSortLabel
      active={orderBy === col}
      direction={orderBy === col ? order : "asc"}
      onClick={() => handleSort(col)}
    >
      {label}
    </TableSortLabel>
  );

  return (
    <div className={classes.root}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Typography variant="h6">{i18n.t("panel.title")}</Typography>
          {scope && (
            <Chip
              size="small"
              color="primary"
              label={`Vista: ${scope.whitelabelName || "marca"}`}
            />
          )}
        </div>
        <Tooltip title={i18n.t("panel.refresh")}>
          <IconButton size="small" onClick={fetchOverview}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>

      {disconnectedCompanies.length > 0 && (
        <Paper
          style={{
            padding: "6px 12px",
            marginTop: 8,
            backgroundColor: "#fef2f2",
            borderLeft: "4px solid #ef4444",
          }}
          elevation={0}
        >
          <Typography variant="body2" style={{ color: "#b91c1c", fontWeight: 600 }}>
            {disconnectedCompanies.length} empresa
            {disconnectedCompanies.length > 1 ? "s" : ""} con WhatsApp desconectado:{" "}
            {disconnectedCompanies.map((c) => c.name).join(", ")}
          </Typography>
        </Paper>
      )}

      <Grid container spacing={1} style={{ marginTop: 4 }}>
        {METRICS.map((m, i) => (
          <Grid item xs key={i} style={{ minWidth: 100 }}>
            <MetricCard label={m.label} value={m.value} classes={classes} />
          </Grid>
        ))}
      </Grid>

      <Paper className={classes.systemLine} elevation={0} variant="outlined">
        {system ? (
          <>
            <span>
              💾 {i18n.t("panel.system.disk")}:{" "}
              {system.diskPct != null
                ? `${system.diskPct}% (${system.diskFreeGB}/${system.diskTotalGB}GB)`
                : i18n.t("panel.system.error")}
            </span>
            <span>🧠 {i18n.t("panel.system.mem")}: {system.memUsedPct}%</span>
            <span>
              ⚙️ {i18n.t("panel.system.load")}: {system.load1}/{system.load5}/{system.load15} ({system.cpuCount}c)
            </span>
            <span>📂 {i18n.t("panel.system.public")}: {system.publicSizeGB}GB</span>
            <span>⏱ {i18n.t("panel.system.uptime")}: {system.uptimeH}h</span>
            {system.alerts &&
              [...(system.alerts.critical || []), ...(system.alerts.warning || [])].map(
                (a, idx) => (
                  <Chip key={idx} size="small" label={a} style={{ backgroundColor: "#fee2e2", color: "#b91c1c" }} />
                )
              )}
          </>
        ) : (
          <CircularProgress size={16} />
        )}
      </Paper>

      <div className={classes.toolbar}>
        {/* feat_242: buscador aislado (memo + guard de focus) */}
        <SearchInput value={search} onChange={setSearch} />
        <div className={classes.filterChips}>
          <Chip
            size="small"
            label={i18n.t("panel.filterAll")}
            color={statusFilter === "all" ? "primary" : "default"}
            onClick={() => setStatusFilter("all")}
          />
          {OP_STATUS_KEYS.map((s) => (
            <Chip
              key={s}
              size="small"
              label={i18n.t(`operationStatus.${s}`)}
              color={statusFilter === s ? "primary" : "default"}
              onClick={() => setStatusFilter(s)}
              style={
                statusFilter === s
                  ? {}
                  : { borderLeft: `3px solid ${OPERATION_STATUS_META[s].color}` }
              }
            />
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<AddIcon />}
          onClick={openCreate}
        >
          {i18n.t("panel.actions.create")}
        </Button>
      </div>

      <Paper className={classes.tableWrap}>
        <Table size="small" className={classes.zebra} stickyHeader>
          <TableHead className={classes.stickyHead}>
            <TableRow>
              <TableCell>{sortLabel("id", i18n.t("panel.col.id"))}</TableCell>
              <TableCell>{sortLabel("name", i18n.t("panel.col.name"))}</TableCell>
              <TableCell>Marca</TableCell>
              <TableCell>{sortLabel("operationStatus", i18n.t("panel.col.status"))}</TableCell>
              <TableCell align="center">{sortLabel("usersOnline", i18n.t("panel.col.usersOnline"))}</TableCell>
              <TableCell align="center">{i18n.t("panel.col.whatsapp")}</TableCell>
              <TableCell align="right">{sortLabel("messages7d", i18n.t("panel.col.messages7d"))}</TableCell>
              <TableCell>{i18n.t("panel.col.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleCompanies.map((c) => {
              const waOk = c.whatsappCount === 0 || c.connectedWhatsapps > 0;
              return (
                <TableRow key={c.id}>
                  <TableCell>{c.id}</TableCell>
                  <TableCell>
                    <Tooltip title={c.notes || ""} arrow disableHoverListener={!c.notes}>
                      <span>{c.name}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {c.whitelabel?.name ? (
                      <Chip label={c.whitelabel.name} size="small" />
                    ) : (
                      <span style={{ color: "gray", fontSize: 11 }}>WAIA</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusChip status={c.operationStatus || "active"} />
                  </TableCell>
                  <TableCell align="center">
                    {c.usersOnline > 0 ? (
                      <span>
                        {c.usersOnline}
                        <span className={classes.onlineDot} />
                      </span>
                    ) : (
                      0
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <span
                      className={classes.statusDot}
                      style={{ backgroundColor: waOk ? "#22c55e" : "#ef4444" }}
                    />
                    {c.connectedWhatsapps}/{c.whatsappCount}
                  </TableCell>
                  <TableCell align="right">{c.messages7d}</TableCell>
                  <TableCell className={classes.actionsCell}>
                    <Tooltip title={i18n.t("panel.actions.edit")}>
                      <IconButton onClick={() => handleEdit(c)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={i18n.t("panel.actions.users")}>
                      <IconButton onClick={() => handleUsers(c)}>
                        <GroupIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={i18n.t("panel.actions.viewAsAdmin")}>
                      <IconButton onClick={() => handleImpersonateAdmin(c)} disabled={impersonating}>
                        <VpnKeyIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={i18n.t("panel.actions.viewAsUser")}>
                      <IconButton onClick={() => handleOpenUserSelector(c)} disabled={impersonating}>
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={i18n.t("panel.actions.clear")}>
                      <IconButton onClick={() => setClearTarget({ id: c.id, name: c.name })}>
                        <DeleteSweepIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={i18n.t("panel.actions.advanced")}>
                      <IconButton onClick={() => handleAdvanced(c)}>
                        <SettingsIcon />
                      </IconButton>
                    </Tooltip>
                    {canDelete && (
                      <Tooltip title="Eliminar empresa">
                        <IconButton onClick={() => openDelete(c)}>
                          <DeleteForeverIcon style={{ color: "#ef4444" }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {visibleCompanies.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" style={{ color: "gray" }}>
                  {companies.length === 0
                    ? "Cargando empresas…"
                    : "Sin empresas que coincidan con el filtro"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* feat_243: signup público = config de plataforma global → solo super superadmin (no resellers) */}
      {canDelete && (
        <div style={{ marginTop: 8 }}>
          <FormControlLabel
            control={
              <Switch
                checked={allowSelfRegistration}
                onChange={(e) => toggleSelfRegistration(e.target.checked)}
                disabled={savingReg}
                color="primary"
                size="small"
              />
            }
            label={<Typography variant="body2">{i18n.t("panel.selfRegistration")}</Typography>}
          />
        </div>
      )}

      {/* feat_235 — Historial y tendencias del sistema (colapsable, lazy) */}
      <Accordion
        style={{ marginTop: 16 }}
        onChange={(_, expanded) => expanded && loadMonitoring()}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" style={{ fontSize: "1rem" }}>
            📈 Historial y tendencias del sistema (últimos 30 días)
          </Typography>
        </AccordionSummary>
        <AccordionDetails style={{ flexDirection: "column" }}>
          {monitoring.prediction?.daysUntilFull != null && (
            <Alert
              severity={monitoring.prediction.daysUntilFull < 30 ? "warning" : "info"}
              style={{ marginBottom: 16 }}
            >
              🔮 Predicción: a la tasa actual, el disco llegaría al 100% en
              aproximadamente <b>{monitoring.prediction.daysUntilFull} días</b>.
            </Alert>
          )}

          {!monitoringLoaded ? (
            <div style={{ textAlign: "center", padding: 24 }}>
              <CircularProgress size={28} />
            </div>
          ) : monitoring.history.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              Sin datos históricos aún. El primer snapshot automático se toma a las
              04:00 ART.
            </Typography>
          ) : (
            <>
              {[
                { key: "disco", label: "💾 Disco (%)", color: "#f44336", field: "diskPct", domain: [0, 100] },
                { key: "memoria", label: "🧠 Memoria (%)", color: "#2196f3", field: "memPct", domain: [0, 100] },
                { key: "load", label: "⚙️ CPU Load (1m)", color: "#4caf50", field: "load1", domain: undefined },
              ].map((chart) => (
                <div key={chart.key}>
                  <Typography variant="subtitle2" style={{ marginTop: 12 }}>
                    {chart.label}
                  </Typography>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart
                      data={monitoring.history.map((s) => ({
                        date: new Date(s.createdAt).toLocaleDateString("es-AR", {
                          day: "numeric",
                          month: "short",
                        }),
                        value: parseFloat(s[chart.field]),
                      }))}
                    >
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis dataKey="date" fontSize={11} />
                      <YAxis domain={chart.domain} fontSize={11} width={36} />
                      <RechartTooltip />
                      <Line type="monotone" dataKey="value" name={chart.key} stroke={chart.color} dot={false} strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ))}

              <Typography
                variant="caption"
                style={{ marginTop: 16, display: "block", color: "gray" }}
              >
                Snapshots diarios · Retención 90 días · API disponible en
                /superadmin/system-health/history
              </Typography>
            </>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Full editor modal (feat_232) */}
      <CompanyEditModal
        open={editModal.open}
        company={editModal.company}
        initialTab={editModal.initialTab}
        onClose={closeEditModal}
        onSaved={fetchOverview}
      />

      {/* Ver como usuario (feat_232) */}
      <UserSelectorModal
        open={!!userSelectorCompany}
        company={userSelectorCompany}
        onClose={() => setUserSelectorCompany(null)}
        onImpersonate={handleImpersonateUser}
      />

      {/* Ver como admin — selector cuando hay >1 admin (feat_233) */}
      <UserSelectorModal
        open={!!adminSelectorCompany}
        company={adminSelectorCompany}
        endpoint="admins-brief"
        title={`Ver como admin — ${adminSelectorCompany?.name || ""}`}
        onClose={() => setAdminSelectorCompany(null)}
        onImpersonate={handleImpersonateAdminPick}
      />

      {/* Clear data dialog */}
      <Dialog open={!!clearTarget} onClose={() => setClearTarget(null)}>
        <DialogTitle>Limpiar datos de prueba</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Esto eliminará todos los <strong>contactos, tickets y mensajes</strong> de{" "}
            <strong>{clearTarget?.name}</strong>. La configuración, usuarios, conexiones y
            colas se mantendrán.
            <br />
            <br />
            Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearTarget(null)} disabled={clearing}>
            Cancelar
          </Button>
          <Button onClick={handleClearData} color="secondary" variant="contained" disabled={clearing}>
            {clearing ? <CircularProgress size={20} /> : "Eliminar datos"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Eliminar empresa (feat_241) — hard delete con confirmación por nombre exacto */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle style={{ color: "#ef4444" }}>Eliminar empresa permanentemente</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            Esto eliminará <strong>todos los datos</strong> de{" "}
            <strong>{deleteTarget?.name}</strong>: contactos, tickets, mensajes, usuarios,
            conexiones, colas y configuración.
            <br />
            <br />
            <span style={{ color: "#ef4444", fontWeight: 600 }}>
              Esta acción es IRREVERSIBLE.
            </span>
          </DialogContentText>
          <Typography variant="body2" style={{ marginTop: 12, marginBottom: 4 }}>
            Escribí el nombre de la empresa para confirmar:
          </Typography>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder={deleteTarget?.name}
            value={deleteConfirm}
            onChange={(e) => setDeleteConfirm(e.target.value)}
            disabled={deleting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteCompany}
            variant="contained"
            disabled={deleting || deleteConfirm !== deleteTarget?.name}
            style={{
              backgroundColor: deleteConfirm === deleteTarget?.name ? "#ef4444" : undefined,
              color: deleteConfirm === deleteTarget?.name ? "#fff" : undefined,
            }}
          >
            {deleting ? <CircularProgress size={20} /> : "Eliminar permanentemente"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SuperAdminPanel;
